import { useEffect, useRef, useCallback } from "react";
import { api } from "@/lib/api";

// Lazily loads the Google reCAPTCHA v3 script once per page load, then exposes
// an `execute(action)` function that returns a token. Silent - no visible
// widget. Returns "" if reCAPTCHA is disabled server-side (dev / preview).

let sitePromise = null;
let scriptPromise = null;

function fetchSiteKey() {
  if (!sitePromise) {
    sitePromise = api.get("/config").then((r) => r.data || {}).catch(() => ({}));
  }
  return sitePromise;
}

function loadScript(siteKey) {
  if (!siteKey) return Promise.resolve(false);
  if (typeof window !== "undefined" && window.grecaptcha && window.grecaptcha.execute) {
    return Promise.resolve(true);
  }
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const existing = document.querySelector(`script[data-dms-recaptcha="1"]`);
    if (existing) { existing.addEventListener("load", () => resolve(true)); return; }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.dataset.dmsRecaptcha = "1";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function useRecaptcha() {
  const siteKeyRef = useRef("");
  const enforceRef = useRef(false);
  const readyRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    fetchSiteKey().then((cfg) => {
      if (!mounted) return;
      siteKeyRef.current = cfg.recaptcha_site_key || "";
      enforceRef.current = !!cfg.recaptcha_enforce;
      if (siteKeyRef.current) {
        loadScript(siteKeyRef.current).then((ok) => { readyRef.current = ok; });
      }
    });
    return () => { mounted = false; };
  }, []);

  const execute = useCallback(async (action) => {
    if (!siteKeyRef.current) return "";
    if (!window.grecaptcha) {
      const ok = await loadScript(siteKeyRef.current);
      if (!ok) return "";
    }
    return new Promise((resolve) => {
      try {
        window.grecaptcha.ready(async () => {
          try {
            const token = await window.grecaptcha.execute(siteKeyRef.current, { action });
            resolve(token || "");
          } catch {
            resolve("");
          }
        });
      } catch {
        resolve("");
      }
    });
  }, []);

  return { execute, ready: readyRef, siteKey: siteKeyRef, enforce: enforceRef };
}
